import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, Activity, CheckCircle2, Play } from 'lucide-react';

export default function MySessions() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [cases, setCases] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      try {
        setLoading(true);
        const apiUrl = process.env.REACT_APP_BACKEND_URL;
        
        // Fetch sessions
        const sessionRes = await axios.get(`${apiUrl}/api/v1/clinicalsim/sessions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const sessionList = sessionRes.data?.sessions || [];
        setSessions(sessionList);

        // Fetch cases to map case_id to case title
        const caseRes = await axios.get(`${apiUrl}/api/v1/clinicalsim/cases`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const caseMap = {};
        caseRes.data?.cases?.forEach(c => {
          caseMap[c.case_id] = c;
        });
        setCases(caseMap);

      } catch (err) {
        console.error('Failed to load sessions:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-800 tracking-tight">My Sessions</h1>
        <p className="text-slate-500 mt-1">History of your clinical simulation attempts.</p>
      </div>

      {sessions.length === 0 ? (
        <div className="bg-white rounded-3xl border border-slate-200 p-12 text-center shadow-sm">
          <Activity className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-700 mb-2">No sessions yet</h2>
          <p className="text-slate-500 mb-6">Start a clinical case from the training library to see your history here.</p>
          <button 
            onClick={() => navigate('/cs/library')}
            className="px-6 py-3 bg-violet-500 hover:bg-violet-600 text-white rounded-xl font-bold shadow-sm transition-all"
          >
            Browse Library
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="py-4 px-6 text-xs font-bold text-slate-500 uppercase tracking-wider">Case</th>
                <th className="py-4 px-6 text-xs font-bold text-slate-500 uppercase tracking-wider">Date</th>
                <th className="py-4 px-6 text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="py-4 px-6 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sessions.map((session) => {
                const caseInfo = cases[session.case_id] || { title: 'Unknown Case', patient: {} };
                const isCompleted = session.status === 'completed';
                
                return (
                  <tr key={session.session_id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-bold text-sm">
                          {caseInfo.patient?.initials || 'PT'}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-slate-800">{caseInfo.title}</p>
                          <p className="text-xs text-slate-500 font-medium">Attempt #{session.attempt_number}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-1.5 text-sm text-slate-600">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        {new Date(session.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      {isCompleted ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-violet-50 text-violet-700 border border-violet-100 text-xs font-bold">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Completed
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-50 text-amber-700 border border-amber-100 text-xs font-bold">
                          <Clock className="w-3.5 h-3.5" /> In Progress
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-6 text-right">
                      <button
                        onClick={() => navigate(`/cs/sessions/${session.session_id}`)}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                          isCompleted
                            ? 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            : 'bg-violet-50 text-violet-600 hover:bg-violet-100'
                        }`}
                      >
                        {isCompleted ? 'View details' : 'Resume'}
                        {!isCompleted && <Play className="w-3.5 h-3.5 fill-current" />}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
